/**
 * Per-user Showcase ("Sample workspace") session ID. Stable derivation
 * from the Clerk userId. Sent to Convex as `showcaseSessionId` so the
 * `demo*` tables can scope sample data per user; the wire format and
 * `demo:` string prefix stay unchanged.
 *
 * For the rare case where there's no signed-in user (we're behind Clerk
 * so this shouldn't happen in normal flow), fall back to a localStorage
 * UUID so the sample workspace still has a stable session key.
 *
 * F19.6 — fallback rotation. `rotateShowcaseFallbackSessionId` is
 * called from the `MsalSessionBinder` `onClear` callback whenever
 * Clerk transitions sign-out / user-swap, so the next signed-out
 * viewer of the showcase gets a fresh `demo:fallback:<uuid>` and
 * cannot scope-leak into the prior signed-out viewer's demo rows.
 * (The `clearMsalSession` default also drops every `repancy:` key
 * including this one, so this helper is an explicit + asserted
 * version of the same behaviour for unit tests.)
 */

import { useUser } from '@clerk/react';
import { useMemo } from 'react';

export const SHOWCASE_FALLBACK_KEY = 'repancy:showcaseFallbackSessionId';
const FALLBACK_KEY = SHOWCASE_FALLBACK_KEY;
const LEGACY_FALLBACK_KEY = 'repancy:demoFallbackSessionId';

/**
 * F19.6 — drop the persisted fallback session id so the next signed-
 * out viewer mints a fresh UUID on first access. Safe to call when
 * no key is present (idempotent). Returns `true` iff a key was
 * actually removed — useful for `onClear` telemetry.
 */
export function rotateShowcaseFallbackSessionId(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const had =
      window.localStorage.getItem(FALLBACK_KEY) !== null ||
      window.localStorage.getItem(LEGACY_FALLBACK_KEY) !== null;
    window.localStorage.removeItem(FALLBACK_KEY);
    window.localStorage.removeItem(LEGACY_FALLBACK_KEY);
    return had;
  } catch {
    return false;
  }
}

function fallbackSessionId(): string {
  if (typeof window === 'undefined') return 'demo:ssr';
  try {
    const legacy = window.localStorage.getItem(LEGACY_FALLBACK_KEY);
    if (legacy) {
      window.localStorage.setItem(FALLBACK_KEY, legacy);
      window.localStorage.removeItem(LEGACY_FALLBACK_KEY);
      return legacy;
    }
    let id = window.localStorage.getItem(FALLBACK_KEY);
    if (!id) {
      id = `demo:fallback:${crypto.randomUUID()}`;
      window.localStorage.setItem(FALLBACK_KEY, id);
    }
    return id;
  } catch {
    return 'demo:nostorage';
  }
}

export function useShowcaseSessionId(): string {
  const { user } = useUser();
  return useMemo(() => {
    if (user?.id) return `demo:user:${user.id}`;
    return fallbackSessionId();
  }, [user?.id]);
}
